import type { KitchenBoardPastPlate, KitchenBoardPlate } from "@/lib/api/types";
import { isOffPremise } from "./channel";

/**
 * READY LEAVES THE SCREENS (T2, D167) — a DISPLAY rule, computed here.
 *
 * The database keeps READY and nothing writes SERVED on a timer (e2 was
 * refused). A READY plate whose `readyAt + N` minutes is in the past leaves
 * the lanes and is listed in the recall drawer «المُنجز مؤخرًا» instead, with
 * its hand-over and «إظهار». N = 0 means never; the server validates N above
 * the pass target, so a card never vanishes the moment it turns «يبرد».
 *
 * DINE-IN ONLY: a bag on an OPEN off-premise bill is waiting for its courier
 * and stays until it is handed over or the bill closes (packer, cashier).
 *
 * PER PLATE on the station view and the floor's Kitchen tab (`splitAgedOff`);
 * PER ORDER on the order view (`splitAgedOffByOrder`, gate P4 #1).
 */

/** What an older API (no `readyAgeOffMinutes`) means: never. */
export const AGE_OFF_NEVER = 0;

export function readyAgeOffMinutesOf(value: number | null | undefined): number {
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : AGE_OFF_NEVER;
}

/** Has this plate aged off the lanes? */
export function isAgedOff(
  plate: Pick<KitchenBoardPlate, "fulfillmentState" | "readyAt" | "channel">,
  minutes: number,
  now: number,
): boolean {
  if (minutes <= 0) return false;
  if (plate.fulfillmentState !== "READY" || !plate.readyAt) return false;
  if (isOffPremise(plate.channel)) return false;
  const readyAt = new Date(plate.readyAt).getTime();
  if (Number.isNaN(readyAt)) return false;
  return now - readyAt > minutes * 60_000;
}

/**
 * Split the board's plates into what the lanes draw and what aged off.
 * `pinned` are plate ids «إظهار» brought back on THIS device: they stay in
 * their lane whatever their age.
 */
export function splitAgedOff<T extends KitchenBoardPlate>(
  plates: readonly T[],
  minutes: number,
  now: number,
  pinned: ReadonlySet<string> = new Set(),
): { live: T[]; agedOff: T[] } {
  const live: T[] = [];
  const agedOff: T[] = [];
  for (const plate of plates) {
    if (!pinned.has(plate.id) && isAgedOff(plate, minutes, now)) agedOff.push(plate);
    else live.push(plate);
  }
  return { live, agedOff };
}

/**
 * A retained «مدفوع» card (T7): a plate a close collapsed while it cooked.
 * The server returns it while `servedAt` (the close) is within N, or within
 * the business day at N = 0; between two reads the device applies the same
 * N so a card does not outstay it on a quiet board. «إخفاء» drops it early.
 */
export function isRetainedVisible(
  plate: Pick<KitchenBoardPastPlate, "id" | "servedAt">,
  minutes: number,
  now: number,
  hidden: ReadonlySet<string> = new Set(),
): boolean {
  if (hidden.has(plate.id)) return false;
  if (minutes <= 0 || !plate.servedAt) return true;
  const servedAt = new Date(plate.servedAt).getTime();
  if (Number.isNaN(servedAt)) return true;
  return now - servedAt <= minutes * 60_000;
}

/**
 * The retained cards the board draws (T7; gate P4 #2) — EVERY plate a close
 * collapsed while it cooked, dine-in included (plan §4.6's predicate, the
 * server's `retainedPaid`). A dine-in bill closed over a cooking plate is a
 * table that asked for the cheque while its last plate cooked: dropping the
 * card would take that food off the cook's screen. The card is worded by the
 * bill's state (`retainedMarkOf`), never assumed paid.
 *
 * They leave by one rule whatever the channel: N after the close (the
 * server's window, the business day at N = 0) or «إخفاء» on this device —
 * the off-premise "never" of T2 is about a bag on an OPEN bill.
 */
export function visibleRetained<T extends KitchenBoardPastPlate>(
  plates: readonly T[] | null | undefined,
  minutes: number,
  now: number,
  hidden: ReadonlySet<string> = new Set(),
): T[] {
  return (plates ?? []).filter((plate) => isRetainedVisible(plate, minutes, now, hidden));
}

/** What a retained card says: «مدفوع» on a SETTLED bill, else «أُغلقت الفاتورة». */
export type RetainedMark = "paid" | "billClosed";

export function retainedMarkOf(plate: Pick<KitchenBoardPastPlate, "billStatus">): RetainedMark {
  return plate.billStatus === "SETTLED" ? "paid" : "billClosed";
}

/**
 * THE ORDER VIEW AGES OFF ORDERS, NEVER PLATES (gate P4 #1; T13's «مكتمل»).
 *
 * On «حسب الطلب» an order is one unit. Its READY plates stay on its card for
 * as long as anything in the order is queued or cooking — a drink ready 12
 * minutes before the grill is still part of the order the expediter hands
 * over. A COMPLETE order (every plate on the board READY) leaves the view N
 * minutes after it COMPLETED — max(its last ready, its last void: P8, a void
 * of the last plate still cooking completes the order, so it shows «مكتمل»
 * for N before it goes) — and goes to the recall drawer whole. An off-premise order never ages off (T2), N = 0 never, and a plate
 * «إظهار» pinned on this device keeps its whole order. The STATION view keeps
 * `splitAgedOff` — per plate, the cook's view.
 */
export function isOrderAgedOff(
  plates: readonly Pick<KitchenBoardPlate, "id" | "fulfillmentState" | "readyAt" | "channel">[],
  minutes: number,
  now: number,
  pinned: ReadonlySet<string> = new Set(),
  /** The order's last void instant (ms), or −∞ when none is known. */
  lastVoidAt: number = Number.NEGATIVE_INFINITY,
): boolean {
  if (minutes <= 0 || plates.length === 0) return false;
  let lastReady = Number.NEGATIVE_INFINITY;
  for (const plate of plates) {
    if (pinned.has(plate.id) || isOffPremise(plate.channel)) return false;
    if (plate.fulfillmentState !== "READY" || !plate.readyAt) return false;
    const readyAt = new Date(plate.readyAt).getTime();
    if (Number.isNaN(readyAt)) return false;
    lastReady = Math.max(lastReady, readyAt);
  }
  return now - Math.max(lastReady, lastVoidAt) > minutes * 60_000;
}

/**
 * Each bill's last void instant (ms) from the board's `recentlyVoided` — the
 * server reads it back for N when N is longer than the strike window (P8).
 */
export function lastVoidByOrder(
  voided: readonly Pick<KitchenBoardPastPlate, "invoiceId" | "voidedAt">[] | null | undefined,
): Map<string, number> {
  const last = new Map<string, number>();
  for (const plate of voided ?? []) {
    if (!plate.voidedAt) continue;
    const at = new Date(plate.voidedAt).getTime();
    if (Number.isNaN(at)) continue;
    last.set(plate.invoiceId, Math.max(last.get(plate.invoiceId) ?? Number.NEGATIVE_INFINITY, at));
  }
  return last;
}

/** `splitAgedOff` for the order view: whole bills, in the order sent. */
export function splitAgedOffByOrder<T extends KitchenBoardPlate>(
  plates: readonly T[],
  minutes: number,
  now: number,
  pinned: ReadonlySet<string> = new Set(),
  voided: readonly Pick<KitchenBoardPastPlate, "invoiceId" | "voidedAt">[] | null | undefined = [],
): { live: T[]; agedOff: T[] } {
  const lastVoid = lastVoidByOrder(voided);
  const byOrder = new Map<string, T[]>();
  for (const plate of plates) {
    const order = byOrder.get(plate.invoiceId);
    if (order) order.push(plate);
    else byOrder.set(plate.invoiceId, [plate]);
  }
  const aged = new Set(
    [...byOrder]
      .filter(([id, order]) => isOrderAgedOff(order, minutes, now, pinned, lastVoid.get(id)))
      .map(([id]) => id),
  );
  const live: T[] = [];
  const agedOff: T[] = [];
  for (const plate of plates) (aged.has(plate.invoiceId) ? agedOff : live).push(plate);
  return { live, agedOff };
}
